import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SignIn, SignUp, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import { BusinessProvider } from "@/context/BusinessContext";
import AppShell from "@/components/AppShell";

import Landing from "@/pages/Landing";
import OnboardingBusiness from "@/pages/OnboardingBusiness";
import OnboardingKnowledge from "@/pages/OnboardingKnowledge";
import OnboardingIntegrations from "@/pages/OnboardingIntegrations";
import OnboardingWhatsApp from "@/pages/OnboardingWhatsApp";
import { OnboardingPlaybook } from "@/pages/Playbook";
import OnboardingLayout from "@/components/OnboardingLayout";
import Dashboard from "@/pages/Dashboard";
import Evals from "@/pages/Evals";
import Conversations from "@/pages/Conversations";
import Requests from "@/pages/Requests";
import Customers from "@/pages/Customers";
import AgentActivity from "@/pages/AgentActivity";
import WorkspacesHub from "@/pages/WorkspacesHub";
import Placeholder from "@/pages/Placeholder";

function AuthScreen({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--canvas)" }}>
      {children}
    </div>
  );
}

function Protected({ children }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut><RedirectToSignIn /></SignedOut>
    </>
  );
}

const Shell = (el) => <Protected><AppShell>{el}</AppShell></Protected>;

const OnbStep = (step, title, desc) => (
  <Protected>
    <OnboardingLayout current={step} title={title}>
      <div className="panel p-8 text-sm w-full" style={{ color: "var(--text-2)" }} data-testid={`onboarding-${step}`}>{desc}</div>
    </OnboardingLayout>
  </Protected>
);

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BusinessProvider>
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { borderRadius: 0, background: "#151a21", border: "1px solid #29313b", color: "#f4f2ec" } }} />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/sign-in/*" element={<AuthScreen><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" /></AuthScreen>} />
            <Route path="/sign-up/*" element={<AuthScreen><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthScreen>} />

            <Route path="/onboarding/business" element={<Protected><OnboardingBusiness /></Protected>} />
            <Route path="/onboarding/playbook" element={<Protected><OnboardingPlaybook /></Protected>} />
            <Route path="/onboarding/knowledge" element={<Protected><OnboardingKnowledge /></Protected>} />
            <Route path="/onboarding/integrations" element={<Protected><OnboardingIntegrations /></Protected>} />
            <Route path="/onboarding/whatsapp" element={<Protected><OnboardingWhatsApp /></Protected>} />
            <Route path="/onboarding/test" element={OnbStep("test", "Test", "Full agent test harness arrives with Phase 9.")} />
            <Route path="/onboarding/complete" element={<Navigate to="/app" replace />} />

            <Route path="/app" element={<Protected><WorkspacesHub /></Protected>} />
            <Route path="/app/dashboard" element={Shell(<Dashboard />)} />
            <Route path="/app/evals" element={Shell(<Evals />)} />
            <Route path="/app/conversations" element={Shell(<Conversations />)} />
            <Route path="/app/conversations/:id" element={Shell(<Conversations />)} />
            <Route path="/app/requests" element={Shell(<Requests />)} />
            <Route path="/app/customers" element={Shell(<Customers />)} />
            <Route path="/app/knowledge" element={Shell(<Placeholder title="Knowledge" phase="Phase 4" description="Document upload (Cloudflare R2) + Chroma RAG arrives in Phase 4." />)} />
            <Route path="/app/integrations" element={Shell(<Placeholder title="Integrations" phase="Phase 5" description="Trello via Composio arrives in Phase 5." />)} />
            <Route path="/app/agent-activity" element={Shell(<AgentActivity />)} />
            <Route path="/app/business" element={Shell(<Placeholder title="Business" phase="Workspace" />)} />
            <Route path="/app/demo" element={Shell(<Placeholder title="Demo" phase="Phase 11" description="Shared demo number binding + full reset arrives in Phase 11." />)} />
            <Route path="/app/settings" element={Shell(<Placeholder title="Settings" phase="Settings" />)} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BusinessProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
